import React, { useState, useEffect } from 'react';
import { Layout } from '../components/Layout';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Button } from '../components/ui/button';
import axios from 'axios';
import {
  ShieldCheck, LogIn, LogOut as LogOutIcon, ShoppingCart, X,
  Wallet, RotateCcw, User, Filter, RefreshCw, Activity
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const actionLabels = {
  create: 'Criação',
  update: 'Atualização',
  delete: 'Exclusão',
  cancel: 'Cancelamento',
  pay: 'Pagamento',
  receive: 'Recebimento',
  login: 'Login',
  logout: 'Logout'
};

const entityLabels = {
  sale: 'Venda',
  payable: 'Conta a Pagar',
  receivable: 'Conta a Receber',
  product: 'Produto',
  user: 'Usuário',
  return: 'Devolução',
  purchase_note: 'Nota de Entrada',
  session: 'Sessão',
  customer: 'Cliente',
  supplier: 'Fornecedor'
};

const iconForAction = (action, entity) => {
  if (action === 'login') return <LogIn size={16} className="text-green-600" />;
  if (action === 'logout') return <LogOutIcon size={16} className="text-slate-500" />;
  if (action === 'cancel') return <X size={16} className="text-red-600" />;
  if (action === 'pay' || action === 'receive') return <Wallet size={16} className="text-blue-600" />;
  if (entity === 'sale') return <ShoppingCart size={16} className="text-blue-600" />;
  if (entity === 'return') return <RotateCcw size={16} className="text-orange-600" />;
  return <Activity size={16} className="text-slate-500" />;
};

const actionColor = (action) => ({
  create: 'bg-green-100 text-green-700',
  update: 'bg-blue-100 text-blue-700',
  delete: 'bg-red-100 text-red-700',
  cancel: 'bg-red-100 text-red-700',
  pay: 'bg-blue-100 text-blue-700',
  receive: 'bg-green-100 text-green-700',
  login: 'bg-green-100 text-green-700',
  logout: 'bg-slate-100 text-slate-700'
}[action] || 'bg-slate-100 text-slate-700');

export const AuditPage = () => (
  <Layout>
    <AuditContent />
  </Layout>
);

export const AuditContent = () => {
  const [logs, setLogs] = useState([]);
  const [summary, setSummary] = useState(null);
  const [filters, setFilters] = useState({ action: 'all', entity: 'all', days: '7' });
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.action !== 'all') params.set('action', filters.action);
      if (filters.entity !== 'all') params.set('entity', filters.entity);
      params.set('days', filters.days);
      params.set('limit', '300');

      const [logsRes, sumRes] = await Promise.all([
        axios.get(`${API}/audit-logs?${params.toString()}`, { withCredentials: true }),
        axios.get(`${API}/audit-logs/summary?days=${filters.days}`, { withCredentials: true })
      ]);
      setLogs(logsRes.data);
      setSummary(sumRes.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filters]);

  const formatDetail = (log) => {
    const d = log.details || {};
    const parts = [];
    if (d.total !== undefined) parts.push(`Total: R$ ${Number(d.total).toFixed(2)}`);
    if (d.amount !== undefined) parts.push(`Valor: R$ ${Number(d.amount).toFixed(2)}`);
    if (d.items_count !== undefined) parts.push(`${d.items_count} item(s)`);
    if (d.reason) parts.push(`Motivo: ${d.reason}`);
    if (d.payment_method) parts.push(`Pgto: ${d.payment_method}`);
    if (d.supplier_name) parts.push(d.supplier_name);
    if (d.customer_name) parts.push(d.customer_name);
    if (d.role) parts.push(`Role: ${d.role}`);
    if (d.discount && d.discount > 0) parts.push(`Desc: R$ ${Number(d.discount).toFixed(2)}`);
    return parts.join(' · ');
  };

  return (
    <>
      <div className="p-8" data-testid="audit-page">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-4xl font-black text-slate-900 tracking-tight flex items-center gap-3">
              <ShieldCheck className="text-blue-600" size={36} /> Auditoria
            </h1>
            <p className="text-base text-slate-600 mt-2">Histórico completo de operações no sistema</p>
          </div>
          <Button onClick={load} variant="outline" disabled={loading}>
            <RefreshCw size={16} className={`mr-2 ${loading ? 'animate-spin' : ''}`} /> Atualizar
          </Button>
        </div>

        {summary && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-white rounded-md border border-slate-200 p-4">
              <p className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500">Eventos</p>
              <p className="text-2xl font-bold text-blue-600 mt-1">{summary.total_events}</p>
              <p className="text-xs text-slate-500 mt-1">últimos {filters.days} dia(s)</p>
            </div>
            <div className="bg-white rounded-md border border-slate-200 p-4 md:col-span-3">
              <p className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500 mb-2">Atividade por Usuário</p>
              <div className="flex flex-wrap gap-2">
                {summary.by_user_action.slice(0, 8).map((s, i) => (
                  <div key={i} className="bg-slate-50 border border-slate-200 rounded px-3 py-1.5 text-xs">
                    <span className="font-bold text-slate-700">{s.user_name}</span>
                    <span className="text-slate-500"> · {actionLabels[s.action] || s.action}: </span>
                    <span className="font-bold text-blue-600">{s.count}</span>
                  </div>
                ))}
                {summary.by_user_action.length === 0 && <p className="text-sm text-slate-500">Sem atividade no período</p>}
              </div>
            </div>
          </div>
        )}

        <div className="bg-white rounded-md border border-slate-200 p-4 mb-4">
          <div className="flex items-center gap-2 mb-3">
            <Filter size={18} className="text-slate-500" />
            <h3 className="font-bold text-slate-700">Filtros</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="space-y-1">
              <Label className="text-xs">Ação</Label>
              <Select value={filters.action} onValueChange={(v) => setFilters({ ...filters, action: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas</SelectItem>
                  <SelectItem value="create">Criação</SelectItem>
                  <SelectItem value="cancel">Cancelamento</SelectItem>
                  <SelectItem value="delete">Exclusão</SelectItem>
                  <SelectItem value="pay">Pagamento</SelectItem>
                  <SelectItem value="receive">Recebimento</SelectItem>
                  <SelectItem value="login">Login</SelectItem>
                  <SelectItem value="logout">Logout</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Entidade</Label>
              <Select value={filters.entity} onValueChange={(v) => setFilters({ ...filters, entity: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas</SelectItem>
                  <SelectItem value="sale">Venda</SelectItem>
                  <SelectItem value="payable">Conta a Pagar</SelectItem>
                  <SelectItem value="receivable">Conta a Receber</SelectItem>
                  <SelectItem value="return">Devolução</SelectItem>
                  <SelectItem value="session">Sessão (Login/Logout)</SelectItem>
                  <SelectItem value="product">Produto</SelectItem>
                  <SelectItem value="user">Usuário</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Período</Label>
              <Select value={filters.days} onValueChange={(v) => setFilters({ ...filters, days: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">Hoje</SelectItem>
                  <SelectItem value="7">Últimos 7 dias</SelectItem>
                  <SelectItem value="30">Últimos 30 dias</SelectItem>
                  <SelectItem value="90">Últimos 90 dias</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-md border border-slate-200 overflow-hidden">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Data/Hora</th>
                <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Usuário</th>
                <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Ação</th>
                <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Entidade</th>
                <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Detalhes</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {logs.length === 0 && (
                <tr><td colSpan={5} className="text-center py-8 text-slate-400">Nenhum evento no período</td></tr>
              )}
              {logs.map((log) => (
                <tr key={log.id} className="hover:bg-slate-50">
                  <td className="px-4 py-3 text-sm text-slate-600 whitespace-nowrap">
                    {new Date(log.created_at).toLocaleString('pt-BR')}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 rounded-full bg-blue-600 text-white text-xs flex items-center justify-center font-bold">
                        {(log.user_name || '?').charAt(0).toUpperCase()}
                      </div>
                      <span className="font-medium text-sm text-slate-700">{log.user_name || 'Sistema'}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`text-xs px-2 py-1 rounded font-bold uppercase tracking-wider inline-flex items-center gap-1 ${actionColor(log.action)}`}>
                      {iconForAction(log.action, log.entity)}
                      {actionLabels[log.action] || log.action}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-slate-700">
                    {entityLabels[log.entity] || log.entity}
                    {log.entity_id && <span className="text-xs text-slate-400 ml-1">#{log.entity_id.substring(0, 6)}</span>}
                  </td>
                  <td className="px-4 py-3 text-sm text-slate-600">{formatDetail(log) || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
};
